import { axiosInstanceWebsite, axiosInstanceAnalytics, axiosInstanceAnalyticsExperiments } from "./instance";
import {
    AnalyticsFunnelPayload,
    AbandonedCartsPayload,
    AxiosResponse,
    FunnelPayload,
    MultiFunnelPayload,
    WebsiteCreditsEstimateResponse,
} from "./interface";

const SECTIONS_GENERATED_CHUNK_SIZE = 75;

export interface SectionsGeneratedResponseItem {
    done: boolean;
    website_id: string;
    has_errors?: boolean;
    error_sections?: string[];
    generation_status?: string | null;
    failure_stage?: string | null;
    failure_reason?: string | null;
    failure_code?: string | null;
    retry_available?: boolean;
    recovery_action?: string | null;
}

const chunkArray = <T>(items: T[], chunkSize: number): T[][] => {
    const chunks: T[][] = [];
    for (let index = 0; index < items.length; index += chunkSize) {
        chunks.push(items.slice(index, index + chunkSize));
    }
    return chunks;
};

/**
 * @deprecated Legacy single-funnel creation endpoint.
 * Active onboarding uses `postBulkFunnels` so product creation, templates,
 * promotions, and async generation share one contract.
 */
export const postFunnel = async (payload: FunnelPayload) => {
    const response = await axiosInstanceWebsite.post('/websites', payload);
    return response.data;
};

export interface BulkFunnelResponseItem {
    id: string;
    owner_id?: string;
    brand_name?: string;
    sale_angle?: { name: string; description: string };
    status?: string;
    [key: string]: any;
}

export const postBulkFunnels = async (payload: MultiFunnelPayload): Promise<BulkFunnelResponseItem[]> => {
    const response = await axiosInstanceWebsite.post('/websites/bulk', payload);
    return Array.isArray(response.data) ? response.data : response.data?.data ?? [];
};

export const postBulkFunnelsEstimate = async (
    payload: MultiFunnelPayload
): Promise<WebsiteCreditsEstimateResponse> => {
    const response = await axiosInstanceWebsite.post('/websites/bulk/estimate', payload);
    return response.data;
};

export const duplicateFunnel = async (websiteId: string) => {
    const response = await axiosInstanceWebsite.post(`/websites/${websiteId}/clone`);
    return response.data;
};

export const updateFunnelName = async (
    websiteId: string,
    nameFunnel: string,
    existingConfigurations: Record<string, unknown> = {}
) => {
    const response = await axiosInstanceWebsite.put(`/websites/${websiteId}`, {
        configurations: {
            ...existingConfigurations,
            name_funnel: nameFunnel,
        },
    });
    return response.data;
};

export const getAnalyticsFunnel = async (data: AnalyticsFunnelPayload) => {
    const response = await axiosInstanceAnalytics.post(`/dashboard-analytics`, data);
    return response.data;
};

export interface FunnelSalesSummary {
    sales_count: number;
    revenue_paid?: number | string;
    revenue?: number | string;
    currency_code?: string | null;
    conversion_rate?: number | string | null;
    average_ticket?: number | string | null;
}

export const getFunnelSalesSummary = async (websiteId: string, createdAt?: string): Promise<FunnelSalesSummary> => {
    const response = await axiosInstanceWebsite.get(`/websites/${websiteId}/sales-summary`, {
        params: createdAt ? { created_at: createdAt } : undefined,
    });
    return response.data;
};

export const getSectionFunnel = async (websiteId: string, sectionId: string) => {
    const response = await axiosInstanceWebsite.get(`/websites/${websiteId}/sections/${sectionId}`);
    return response.data;
};

export const retrySection = async (websiteId: string, sectionName: string) => {
    const response = await axiosInstanceWebsite.post(
        `/websites/${websiteId}/sections/${sectionName}/retry`
    );
    return response.data;
};

export const recoverFunnelGeneration = async (websiteId: string) => {
    const response = await axiosInstanceWebsite.post(
        `/websites/${websiteId}/generation/recover`
    );
    return response.data;
};

const getSectionsGeneratedChunk = async (websiteIds: string[]): Promise<SectionsGeneratedResponseItem[]> => {
    const response: AxiosResponse = await axiosInstanceWebsite.get(`/websites/sections/generated`, {
        params: {
            website_ids: websiteIds.join(","),
        },
    });
    return Array.isArray(response.data) ? response.data : [];
};

export const getSectionsGenerated = async (data: string[]): Promise<SectionsGeneratedResponseItem[]> => {
    const websiteIds = Array.from(new Set(data.filter(Boolean)));
    if (websiteIds.length === 0) return [];

    const chunks = chunkArray(websiteIds, SECTIONS_GENERATED_CHUNK_SIZE);
    const results = await Promise.allSettled(
        chunks.map((chunk) => getSectionsGeneratedChunk(chunk))
    );

    const fulfilled = results
        .filter((result): result is PromiseFulfilledResult<SectionsGeneratedResponseItem[]> => result.status === "fulfilled")
        .flatMap((result) => result.value);

    if (fulfilled.length > 0) return fulfilled;

    const firstError = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
    throw firstError?.reason ?? new Error("No pudimos consultar el estado de generación de los embudos.");
};

export const getFunnelEventsAnalytics = async (data: AnalyticsFunnelPayload) => {
    const response = await axiosInstanceAnalytics.post(`/funnel-events-analytics`, data);
    return response.data;
};

export interface WebsiteVisitSummary {
    website_id: string;
    visits: number;
    unique_visits: number;
}

export interface WebsiteVisitSummaryBatchResponse {
    summaries: WebsiteVisitSummary[];
    computed_at: string;
}

export const getWebsiteVisitsSummary = async (
    websiteIds: string[],
    createdAt?: string
): Promise<WebsiteVisitSummaryBatchResponse> => {
    const response = await axiosInstanceAnalyticsExperiments.post(`/website-visits-summary`, {
        website_ids: websiteIds,
        ...(createdAt ? { created_at: createdAt } : {}),
    });
    return response.data;
};

export const getAbandonedCarts = async (data: AbandonedCartsPayload) => {
    const response = await axiosInstanceAnalytics.post(`/abandoned-carts`, data);
    return response.data;
};

// Section Templates
export interface SectionTemplate {
    id: number;
    name: string;
    type: string;
    family_key?: string | null;
    section_type: string;
    url: string | null;
    metadata: Record<string, any> | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export const getSectionTemplates = async (type_param?: string, value_params?: string): Promise<SectionTemplate[]> => {
    const params = type_param ? `${type_param}=${value_params}` : '';
    const response = await axiosInstanceWebsite.get(`/websites/section-templates?${params}`);
    return response.data;
};

export const getSectionTemplatesByType = async (type: string): Promise<SectionTemplate[]> => {
    const response = await axiosInstanceWebsite.get(`/websites/section-templates/type/${type}`);
    return response.data;
};

export interface OnboardingCardBinding {
    id: number;
    family_key: string;
    binding_key: string;
    style_id: string;
    palette_fallback: string;
    display_name: string;
    description: string | null;
    preview_url: string;
    thumbnail_url: string;
    sort_order: number;
    is_published_for_users?: boolean;
    is_active?: boolean;
    contract_version: string;
    kind: 'html' | 'image' | 'element';
}

export const getOnboardingCards = async (): Promise<OnboardingCardBinding[]> => {
    const response = await axiosInstanceWebsite.get('/websites/template-studio/onboarding-cards');
    return response.data;
};
